import { useEffect, useState } from "react";
import { courses } from "@/courses/registry";
import { BrandMark, Icon } from "@/ui/components/Icon";
import { StatusRing } from "@/ui/components/Progress";
import { courseStats, lookupFor } from "@/ui/progressStats";
import { LessonSearch } from "./LessonSearch";
import { useAllProgress } from "./useAllProgress";
import { href, useHashRoute } from "./useHashRoute";
import { WebCourse } from "./WebCourse";
import { WebHome } from "./WebHome";
import { WebLesson } from "./WebLesson";

/**
 * Die Browser-Ansicht: alle Kurse, der Fortschritt und die Lektionen zum
 * Lesen. Läuft das Add-in in Word, Excel oder PowerPoint, erscheint statt
 * dessen das schmale Seitenpanel.
 */
export function WebApp() {
  const route = useHashRoute();
  const progress = useAllProgress(courses);
  const [menuOpen, setMenuOpen] = useState(false);

  const routeKey =
    route.type === "lesson"
      ? `${route.courseId}/${route.moduleId}/${route.lessonId}`
      : route.type === "course"
        ? route.courseId
        : "home";

  // Jede Ansicht beginnt oben, das Menü schließt sich nach der Auswahl.
  useEffect(() => {
    window.scrollTo({ top: 0 });
    setMenuOpen(false);
  }, [routeKey]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const activeCourseId = route.type === "home" ? null : route.courseId;
  const course = activeCourseId ? courses.find((entry) => entry.id === activeCourseId) : null;

  let screen;
  if (route.type === "home") {
    screen = <WebHome courses={courses} progress={progress} />;
  } else if (!course) {
    screen = (
      <div className="w-page w-missing">
        <h1>Diesen Kurs gibt es nicht</h1>
        <p>Die Adresse verweist auf keinen der Kurse. Diese gibt es:</p>
        <ul className="w-rows">
          {courses.map((entry) => (
            <li key={entry.id} data-host={entry.host}>
              <a className="w-row" href={href.course(entry.id)}>
                <span className="w-dot" aria-hidden="true" />
                <span className="w-row__text">
                  <span className="w-row__title">{entry.title}</span>
                  <span className="w-row__meta">{entry.modules.length} Module</span>
                </span>
                <Icon name="chevronRight" className="w-row__icon" />
              </a>
            </li>
          ))}
        </ul>
      </div>
    );
  } else if (route.type === "course") {
    screen = <WebCourse course={course} progress={progress} />;
  } else {
    screen = (
      <WebLesson
        course={course}
        moduleId={route.moduleId}
        lessonId={route.lessonId}
        progress={progress.get(course.id)}
      />
    );
  }

  return (
    <div className="web">
      <aside
        className={`w-sidebar ${menuOpen ? "is-open" : ""}`}
        aria-label="Navigation"
        id="w-navigation"
      >
        <div className="w-sidebar__head">
          <a className="w-brand" href={href.home()}>
            <BrandMark />
            <span>Interaktive Office-Kurse</span>
          </a>
          <button
            type="button"
            className="w-iconbutton w-sidebar__close"
            onClick={() => setMenuOpen(false)}
            aria-label="Menü schließen"
          >
            <Icon name="close" />
          </button>
        </div>

        <nav className="w-nav">
          <a
            className="w-nav__item"
            href={href.home()}
            aria-current={route.type === "home" ? "page" : undefined}
          >
            <Icon name="home" />
            Übersicht
          </a>

          <p className="w-nav__group">Kurse</p>
          {courses.map((entry) => {
            const stats = courseStats(entry, lookupFor(progress.get(entry.id)));
            return (
              <a
                key={entry.id}
                className="w-nav__item"
                data-host={entry.host}
                href={href.course(entry.id)}
                aria-current={activeCourseId === entry.id ? "page" : undefined}
              >
                <StatusRing fraction={stats.stepsDone / stats.stepsTotal} size={16} />
                <span className="w-nav__label">{entry.host}</span>
                <span className="visually-hidden">
                  , {stats.percentage} Prozent erledigt
                </span>
              </a>
            );
          })}
        </nav>

        <p className="w-sidebar__note">
          <Icon name="info" />
          <span>
            Gezeigt wird der Fortschritt aus diesem Browser. Was du im Seitenpanel von Office
            erledigst, wird dort gespeichert.
          </span>
        </p>
      </aside>

      {menuOpen ? (
        <div className="w-scrim" onClick={() => setMenuOpen(false)} aria-hidden="true" />
      ) : null}

      <header className="w-topbar">
        <button
          type="button"
          className="w-iconbutton w-topbar__menu"
          onClick={() => setMenuOpen(true)}
          aria-label="Menü öffnen"
          aria-expanded={menuOpen}
          aria-controls="w-navigation"
        >
          <Icon name="menu" />
        </button>
        <a className="w-brand w-topbar__brand" href={href.home()}>
          <BrandMark />
          <span>Office-Kurse</span>
        </a>
        <LessonSearch courses={courses} />
      </header>

      <main className="w-sheet">
        {progress.loaded ? (
          <div className="w-view" key={routeKey}>
            {screen}
          </div>
        ) : (
          <div className="w-page" aria-busy="true" aria-label="Fortschritt wird geladen">
            <span className="skeleton skeleton--title" />
            <span className="skeleton skeleton--panel" />
          </div>
        )}
      </main>
    </div>
  );
}
